import React from "react";
import SimpleSelect from "components/SimpleSelect";
import { useTranslation } from "react-i18next";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import {
  BUILDING_CLASSES,
  BUILDING_TYPES,
  ENERGETICS_STANDARDS,
  LEVEL_OF_REQUIREMENTS,
  QNG_SUPPORTING_DOCS,
} from "utils";
import { LCAClassEnum, LevelOfRequirementsEnum } from "api-client";
import { FormValue } from "../BuildingSettingsStore";

const DGNB23AndQNG = (props: {
  buildingType?: string | null;
  levelOfRequirements?: LevelOfRequirementsEnum | null;
  energeticStandard?: string | null;
  lcaClass?: LCAClassEnum | null;
  onChange?: (value: FormValue, name?: string) => void;
  hasError?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const selectedBuildingType = BUILDING_TYPES.find(
    (item) =>
      item.name === props.buildingType || item.name_de === props.buildingType
  );

  const selectedLevel = LEVEL_OF_REQUIREMENTS.find(
    (item) => item.name === props.levelOfRequirements
  );

  const selectedStandard = ENERGETICS_STANDARDS.find(
    (item) =>
      item.name === props.energeticStandard ||
      item.name_de === props.energeticStandard
  );

  const selectedBuildingClass =
    BUILDING_CLASSES.find((item) => item.name === props.lcaClass) ?? "";

  const isResidentioalBuilding =
    selectedBuildingType?.id === BUILDING_TYPES[0].id;

  const isNonResidentialBuilding =
    selectedBuildingType?.id === BUILDING_TYPES[1].id;

  const onChangeLCAClass = (value: string | undefined | null) => {
    props.onChange?.(
      BUILDING_CLASSES.find((item) => item.id === value)?.name ?? null,
      "lca_class"
    );
  };

  const onChangeBuildingType = (e: BGItem | undefined | null) => {
    props.onChange?.(isEng ? e?.name : e?.name_de, "building_type");
  };

  const onChangeLevel = (e: BGItem | undefined | null) => {
    props.onChange?.(e?.name, "level_of_requirements");
  };

  const onChangeEnergeticStandard = (e: BGItem | undefined | null) => {
    props.onChange?.(isEng ? e?.name : e?.name_de, "energetic_standard");
  };

  React.useEffect(() => {
    if (!props.buildingType) {
      onChangeBuildingType(BUILDING_TYPES[1]);
    }
    if (!props.energeticStandard) {
      onChangeEnergeticStandard(ENERGETICS_STANDARDS[1]);
    }
    if (!props.levelOfRequirements) {
      onChangeLevel(LEVEL_OF_REQUIREMENTS[1]);
    }
  }, []);

  React.useEffect(() => {
    if (isResidentioalBuilding) {
      onChangeLevel(null);
      onChangeLCAClass(null);
    }
    if (isNonResidentialBuilding) {
      onChangeEnergeticStandard(null);
    }
  }, [isResidentioalBuilding, isNonResidentialBuilding]);

  return (
    <div className="bg-white p-4">
      <div className="flex justify-between w-full gap-4">
        <ButtonGroups
          items={BUILDING_TYPES}
          current={selectedBuildingType}
          label={t("buildingSettings.buildingType")}
          required
          containerClassName="w-full"
          groupClassName="!mt-0"
          name="building_type"
          optionClsssName="whitespace-nowrap"
          onChange={onChangeBuildingType}
          showMandatoryAlert={props.hasError && !selectedBuildingType}
        />
        {isNonResidentialBuilding && (
          <ButtonGroups
            containerClassName="mb-4 w-full"
            items={LEVEL_OF_REQUIREMENTS}
            label={t("buildingSettings.levelOfRequirements")}
            required
            groupClassName="!mt-0"
            name="level_of_requirements"
            onChange={onChangeLevel}
            current={selectedLevel}
            showMandatoryAlert={props.hasError && !selectedLevel}
          />
        )}
      </div>
      {isResidentioalBuilding && (
        <ButtonGroups
          containerClassName="my-4 w-full"
          items={ENERGETICS_STANDARDS}
          label={t("buildingSettings.energeticStandard")}
          required
          groupClassName="!mt-0"
          name="energetic_standard"
          onChange={onChangeEnergeticStandard}
          current={selectedStandard}
          showMandatoryAlert={props.hasError && !selectedStandard}
        />
      )}
      {isNonResidentialBuilding && (
        <SimpleSelect
          placeholder={t("buildingSettings.selectClass")}
          options={BUILDING_CLASSES}
          labelName={t("buildingSettings.LCAclass")}
          labelClassName="text-sm leading-5 font-medium"
          current={selectedBuildingClass ? selectedBuildingClass.id : undefined}
          clearOption
          showMandatoryAlert={props.hasError && !selectedBuildingClass}
          required
          noMargin
          name="lca_class"
          onChange={onChangeLCAClass}
          helper={
            <div
              dangerouslySetInnerHTML={{
                __html: t("buildingSettings.lcaClassDescription", {
                  link: QNG_SUPPORTING_DOCS,
                }),
              }}
            ></div>
          }
        />
      )}
    </div>
  );
};

export default DGNB23AndQNG;
